import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, iif, of } from 'rxjs';
import { map, mergeMap, withLatestFrom, catchError } from 'rxjs/operators';

/* notification service */
import { NotificationService } from 'src/app/features/shared/services/notification-service/notification.service';

/* jsonata object class */
import { JSONAtaObject } from 'src/app/features/shared/helper-classes';

/* lodash lib */
import * as lodash from 'lodash';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* navigate action */
import { Navigate } from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* Workflow Management API Service */
import { WorkflowManagementControllerService } from 'src/app/features/shared/sdk/services/workflow-management-controller.service';

/* appointment-data-store actions */
import * as AppointmentDataStoreActions from 'src/app/features/data-stores/appointment-data-store/state/appointment-data-store.actions';

/* api services */
import { AppointmentControllerService } from 'src/app/features/shared/sdk/services';

@Injectable()
export class AppointmentDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private notificationService: NotificationService,
    private workflowManagementApi: WorkflowManagementControllerService,
    private appointmentApiService: AppointmentControllerService,
  ) { 
    // prettier-ignore
    // #region customCode: data-store-effects__constructor__start
    
    // #endregion customCode: data-store-effects__constructor__start

    // prettier-ignore
    // #region customCode: data-store-effects__constructor__end
    
    // #endregion customCode: data-store-effects__constructor__end
  }

  getAppointments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentDataStoreActions.GetAppointments),
      mergeMap((action) => {
        return this.appointmentApiService.find().pipe(
          mergeMap((appointments) => {
            const payload: any = {
              appointments: appointments,
            };
            return [
              AppointmentDataStoreActions.GetAppointmentsSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while fetching data.',
            );
            return [AppointmentDataStoreActions.GetAppointmentsFailure()];
          }),
        );
      }),
    ),
  );

  getAppointmentById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentDataStoreActions.GetAppointmentById),
      mergeMap((action) => {
        return this.appointmentApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((appointment) => {
              const payload: any = {
                appointment: appointment,
              };
              return [
                AppointmentDataStoreActions.GetAppointmentByIdSuccessful({
                  payload,
                }),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [AppointmentDataStoreActions.GetAppointmentByIdFailure()];
            }),
          );
      }),
    ),
  );

  createAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentDataStoreActions.CreateAppointment),
      mergeMap((action) => {
        const params = {
          body: action.payload.appointment,
        };
        return this.appointmentApiService.create(<any>params).pipe(
          mergeMap((appointment) => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been saved successfully.',
            );
            const payload: any = {
              appointment: appointment,
            };
            return [
              AppointmentDataStoreActions.CreateAppointmentSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while saving the data.',
            );
            return [AppointmentDataStoreActions.CreateAppointmentFailure()];
          }),
        );
      }),
    ),
  );

  updateAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentDataStoreActions.UpdateAppointment),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: action.payload.data,
        };
        return this.appointmentApiService.updateById(<any>params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been updated successfully.',
            );
            return [AppointmentDataStoreActions.GetAppointments()];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while updating the data.',
            );
            return [AppointmentDataStoreActions.UpdateAppointmentFailure()];
          }),
        );
      }),
    ),
  );

  deleteAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentDataStoreActions.DeleteAppointment),
      mergeMap((action) => {
        const params = { id: action.payload.id };
        return this.appointmentApiService.deleteById(params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Your data has been deleted successfully.',
            );
            const payload: any = {
              id: action.payload.id,
            };
            return [
              AppointmentDataStoreActions.DeleteAppointmentSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while deleting the data.',
            );
            return [AppointmentDataStoreActions.DeleteAppointmentFailure()];
          }),
        );
      }),
    ),
  );
}
